import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ArrowUpDown, Info, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import PromoTag from '../components/PromoTag';
import FreshnessChip from '../components/FreshnessChip';
import ProviderIdentity, { ProviderLockLink } from '../components/ProviderIdentity';
import { api } from '../lib/api';
import { formatPrice, PRICE_TYPE_LABELS, CATEGORY_LABELS } from '../lib/utils';
import { applySeo, breadcrumbJsonLd, tldProductJsonLd } from '../lib/seo';

export default function TldDetailPage() {
  const { tld: rawTld } = useParams();
  // Kanonische Form: ".de" und "de" führen beide zur gleichen Seite (API normalisiert)
  const tld = decodeURIComponent(rawTld).replace(/^\./, '');
  const tldDisplay = `.${tld}`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [years, setYears] = useState(1);
  const [priceType, setPriceType] = useState('registration');
  const [sortBy, setSortBy] = useState('total');
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    loadData();
  }, [tld, years, priceType, sortBy]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [result, dealsResult] = await Promise.all([
        api.compare(tld, years, priceType, sortBy),
        api.getDeals(tld).catch(() => []),
      ]);
      setData(result);
      setDeals(dealsResult || []);
    } catch (e) {
      console.error('Fehler:', e);
    } finally {
      setLoading(false);
    }
  };

  // SEO: kanonische URL-Form ohne führenden Punkt (/tld/de), nur bei Datenwechsel
  useEffect(() => {
    if (!data) return;
    const tldSlug = tld.replace(/^\./, '');
    const desc = `${tldDisplay}-Domain-Preise vergleichen: Registrierung, Verlängerung und Transfer bei ${data.total_providers || 0} Anbietern. Jetzt den günstigsten Preis finden.`;
    applySeo({
      title: `${tldDisplay} Domain-Preise vergleichen | Domain-Preisvergleich.de`,
      description: desc,
      path: `/tld/${tldSlug}`,
      type: 'website',
      jsonLd: [
        breadcrumbJsonLd([
          { name: 'Startseite', path: '/' },
          { name: tldDisplay, path: `/tld/${tldSlug}` },
        ]),
        tldProductJsonLd(tldDisplay, data.results),
      ],
    });
  }, [tld, data]);

  const bestPrice = data?.results?.[0]?.calculation?.total;  const dealByProvider = (providerId) => deals.find(d => d.provider_id === providerId);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-slate-900">Startseite</Link>
          <span>/</span>
          <span className="text-slate-900 font-medium">{tldDisplay}</span>
        </div>

        {/* Title + Controls */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk'}} data-testid="tld-detail-title">
              {tldDisplay} Preisvergleich
            </h1>
            {data?.tld_info && (
              <p className="text-muted-foreground mt-1">
                {data.tld_info.description} · <Badge variant="outline" className="text-[10px]">{CATEGORY_LABELS[data.tld_info.category]}</Badge>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {data && <span>{data.total_providers} Anbieter gefunden</span>}
          </div>
        </div>

        {/* Filter Controls */}
        <Card className="rounded-2xl mb-6">
          <CardContent className="p-4 md:p-6">
            <div className="grid sm:grid-cols-3 gap-6">
              {/* Price Type */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">Preistyp</label>
                <Tabs value={priceType} onValueChange={setPriceType} data-testid="price-type-switcher">
                  <TabsList className="w-full grid grid-cols-3">
                    <TabsTrigger value="registration" data-testid="price-type-registration-tab" className="text-xs">Registrierung</TabsTrigger>
                    <TabsTrigger value="renewal" data-testid="price-type-renewal-tab" className="text-xs">Verlängerung</TabsTrigger>
                    <TabsTrigger value="transfer" data-testid="price-type-transfer-tab" className="text-xs">Transfer</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Years */}
              <div data-testid="multi-year-control">
                <label className="text-xs font-medium text-muted-foreground mb-2 block">
                  Laufzeit: <span className="font-bold text-slate-900">{years} {years === 1 ? 'Jahr' : 'Jahre'}</span>
                </label>
                <Slider
                  value={[years]}
                  onValueChange={([v]) => setYears(v)}
                  min={1}
                  max={5}
                  step={1}
                  className="mt-3"
                  data-testid="multi-year-slider"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                  <span>1 Jahr</span>
                  <span>5 Jahre</span>
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">Sortierung</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="total">Gesamtpreis</SelectItem>
                    <SelectItem value="effective_yearly">Effektiver Jahrespreis</SelectItem>
                    <SelectItem value="provider">Anbieter (A-Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Price Comparison Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" />
          </div>
        ) : data?.results?.length === 0 ? (
          <Card className="rounded-2xl">
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">Keine Preise für {tldDisplay} gefunden.</p>
              <Link to="/" className="text-sky-600 text-sm mt-2 inline-block">Zurück zur Startseite</Link>
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="price-comparison-table">
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <div className="col-span-3">Anbieter</div>
              <div className="col-span-2 text-right">1. Jahr</div>
              <div className="col-span-2 text-right">Verlängerung/J.</div>
              <div className="col-span-2 text-right font-bold text-slate-700">Gesamt ({years}J.)</div>
              <div className="col-span-1 text-right">Ø/Jahr</div>
              <div className="col-span-2 text-right">Aktion</div>
            </div>

            {/* Table Rows */}
            {data.results.map((row, idx) => {
              const isBest = row.calculation.total != null && row.calculation.total === bestPrice;
              return (
                <div
                  key={row.provider_id}
                  className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 md:px-6 py-4 border-b border-slate-100 last:border-0 transition-colors duration-150 ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'
                  } hover:bg-sky-50/60 ${isBest ? 'border-l-4 border-l-emerald-500' : ''}`}
                  data-testid="price-comparison-table-row"
                >
                  {/* Provider */}
                  <div className="md:col-span-3">
                    <ProviderIdentity provider={row} />
                    <div className="flex items-center gap-2 mt-1 ml-11">
                      <FreshnessChip lastUpdate={row.last_import_at || row.last_crawled_at} dataSource={row.data_source} />
                      {row.tier !== 'pro' && <ProviderLockLink />}
                    </div>
                    {(() => {
                      const deal = row.tier === 'pro' ? dealByProvider(row.provider_id) : null;
                      if (!deal) return null;
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 mt-1 ml-11 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 cursor-help" data-testid="deal-badge">
                                <Tag className="w-3 h-3" /> Deal: {deal.code}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <div className="text-xs space-y-1">
                                <p className="font-semibold">{deal.title}</p>
                                {deal.description && <p>{deal.description}</p>}
                                <p className="text-amber-800 font-medium">Code: {deal.code}</p>
                                {deal.end_date && <p>Gültig bis: {deal.end_date}</p>}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })()}
                  </div>

                  {/* Year 1 Price */}
                  <div className="md:col-span-2 flex md:justify-end items-center">
                    <div className="md:hidden text-xs text-muted-foreground mr-2">1. Jahr:</div>
                    <div className="text-right">
                      <span className="font-semibold text-sm tabular-nums">
                        {formatPrice(row.calculation.breakdown[0]?.final_price)}
                      </span>
                      {row.calculation.breakdown[0]?.promotion_applied && (
                        <div className="text-[10px] text-muted-foreground line-through tabular-nums">
                          {formatPrice(row.calculation.breakdown[0]?.base_price)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Renewal */}
                  <div className="md:col-span-2 flex md:justify-end items-center">
                    <div className="md:hidden text-xs text-muted-foreground mr-2">Verläng.:</div>
                    <span className="text-sm text-muted-foreground tabular-nums">
                      {formatPrice(row.base_renewal)}
                    </span>
                  </div>

                  {/* Total */}
                  <div className="md:col-span-2 flex md:justify-end items-center">
                    <div className="md:hidden text-xs text-muted-foreground mr-2">Gesamt:</div>
                    <span className={`text-base font-bold tabular-nums ${isBest ? 'text-emerald-700' : 'text-slate-900'}`}>
                      {formatPrice(row.calculation.total)}
                    </span>
                  </div>

                  {/* Effective Yearly */}
                  <div className="md:col-span-1 flex md:justify-end items-center">
                    <div className="md:hidden text-xs text-muted-foreground mr-2">Ø/Jahr:</div>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {formatPrice(row.calculation.effective_yearly)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="md:col-span-2 flex items-center justify-end gap-2">
                    {row.has_promotion && row.promotions.length > 0 && (
                      <PromoTag promotion={row.promotions[0]} compact />
                    )}
                    {row.tier === 'pro' && row.affiliate_url && (
                      <a href={row.affiliate_url} target="_blank" rel="nofollow noopener noreferrer">
                        <Button size="sm" variant="outline" className="text-xs gap-1" data-testid="price-comparison-cta-button">
                          Zum Anbieter <ExternalLink className="w-3 h-3" />
                        </Button>
                      </a>
                    )}
                    {row.tier !== 'pro' && row.provider_website && (
                      <a href={row.provider_website} target="_blank" rel="nofollow noopener noreferrer" className="text-[10px] text-slate-400 hover:text-sky-600 inline-flex items-center gap-1">
                        Website <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Price Breakdown Detail */}
        {data?.results?.length > 0 && years > 1 && (
          <Card className="rounded-2xl mt-6">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Info className="w-4 h-4 text-sky-600" />
                Preisaufschlüsselung ({years} Jahre)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">Anbieter</th>
                      {Array.from({length: years}, (_, i) => (
                        <th key={i} className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Jahr {i+1}</th>
                      ))}
                      <th className="text-right py-2 px-3 text-xs font-bold text-slate-700">Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.results.slice(0, 6).map(row => (
                      <tr key={row.provider_id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 px-3 font-medium">{row.provider_name}</td>
                        {row.calculation.breakdown.map((b, i) => (
                          <td key={i} className="text-right py-2 px-3 tabular-nums">
                            <span className={b.promotion_applied ? 'text-emerald-700 font-semibold' : ''}>
                              {formatPrice(b.final_price)}
                            </span>
                            {b.promotion_applied && <span className="text-[9px] text-amber-700 block">Aktion</span>}
                          </td>
                        ))}
                        <td className="text-right py-2 px-3 font-bold tabular-nums">{formatPrice(row.calculation.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
